import { EDITOR_PRIMARY_LIGHT_MAP_HEX } from '../constants/editorPrimaryMapHex.js';

/**
 * Detect the dominant geometry type from a GeoJSON FeatureCollection
 * and map it to the appropriate MapLibre layer style type.
 *
 * Returns:
 *  - 'circle' if the dominant geometry is Point / MultiPoint
 *  - 'line'   if the dominant geometry is LineString / MultiLineString
 *  - 'fill'   if the dominant geometry is Polygon / MultiPolygon
 *  - 'circle' as fallback for empty or unrecognised collections
 */

type StyleType = 'circle' | 'line' | 'fill';

/** GeoJSON geometry type → normalised category */
const GEOMETRY_CATEGORY: Record<string, 'point' | 'line' | 'polygon'> = {
  Point: 'point',
  MultiPoint: 'point',
  LineString: 'line',
  MultiLineString: 'line',
  Polygon: 'polygon',
  MultiPolygon: 'polygon',
};

const CATEGORY_TO_STYLE: Record<string, StyleType> = {
  point: 'circle',
  line: 'line',
  polygon: 'fill',
};

/**
 * Analyse a GeoJSON FeatureCollection and return the best MapLibre style type.
 *
 * The algorithm counts features per normalised geometry category (point / line /
 * polygon) and picks the category with the most features.  Ties are broken with
 * a stable priority: polygon > line > point (because polygons are the most
 * visually dominant and should get fill by default).
 */
export function detectGeometryType(
  fc: { type: string; features: Array<{ geometry?: { type: string } | null }> } | null | undefined,
): StyleType {
  if (!fc || !Array.isArray(fc.features) || fc.features.length === 0) {
    return 'circle'; // safe default for empty collections
  }

  const counts: Record<string, number> = { point: 0, line: 0, polygon: 0 };

  for (const feature of fc.features) {
    if (!feature.geometry) continue;
    const category = GEOMETRY_CATEGORY[feature.geometry.type];
    if (category) {
      counts[category]++;
    }
  }

  // Pick the category with the highest count.
  // Priority order for tie-breaking: polygon > line > point.
  const priority: Array<'polygon' | 'line' | 'point'> = ['polygon', 'line', 'point'];
  let best: 'polygon' | 'line' | 'point' = 'point';
  let bestCount = 0;

  for (const cat of priority) {
    if (counts[cat] > bestCount) {
      best = cat;
      bestCount = counts[cat];
    }
  }

  return CATEGORY_TO_STYLE[best];
}

/**
 * Return the set of unique normalised geometry categories present in the
 * FeatureCollection (e.g. `new Set(['line', 'polygon'])`).
 *
 * Useful for deciding which sub-layers to create.
 */
export function getGeometryCategories(
  fc: { type: string; features: Array<{ geometry?: { type: string } | null }> } | null | undefined,
): Set<'point' | 'line' | 'polygon'> {
  const categories = new Set<'point' | 'line' | 'polygon'>();
  if (!fc || !Array.isArray(fc.features)) return categories;

  for (const feature of fc.features) {
    if (!feature.geometry) continue;
    const category = GEOMETRY_CATEGORY[feature.geometry.type];
    if (category) categories.add(category);
  }

  return categories;
}

/**
 * Build default paint properties for a given style type.
 */
export function getDefaultPaintForType(styleType: StyleType): Record<string, unknown> {
  switch (styleType) {
    case 'circle':
      return {
        'circle-color': EDITOR_PRIMARY_LIGHT_MAP_HEX,
        'circle-radius': 5,
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1,
      };
    case 'line':
      return {
        'line-color': EDITOR_PRIMARY_LIGHT_MAP_HEX,
        'line-width': 2,
      };
    case 'fill':
      return {
        'fill-color': EDITOR_PRIMARY_LIGHT_MAP_HEX,
        'fill-opacity': 0.4,
      };
    default:
      return {};
  }
}
